import { type Card, type Rank, type Suit, fullDeck, sameCard, sortHand } from '../trick/cards.js';
import { seededRandom, shuffled } from '../trick/rng.js';
import { type TrickPlay, followSuit, trickWinner } from '../trick/rules.js';
import {
  BRIDGE_SEATS,
  type BridgeControllers,
  type BridgeSeat as Seat,
  SINGLE_PLAYER,
  clockwiseAfter,
  leftOf,
} from '../bridge/seats.js';

export type { Seat as PirateSeat };

export class IllegalPirateMoveError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'IllegalPirateMoveError';
  }
}

/** `guess`: everyone secretly guesses their tricks. `play`: guesses are revealed and the tricks are played. */
export type PirateRoundPhase = 'guess' | 'play' | 'done';

export interface PirateCompletedTrick {
  readonly leader: Seat;
  readonly plays: readonly TrickPlay<Seat>[];
  readonly winner: Seat;
}

/**
 * One deal of Pirate Bridge: four players, each for themselves, `cardsPerHand` cards each. Everyone
 * guesses how many tricks they will win, simultaneously and hidden until all four are in; the guesses
 * may add up to more or fewer than the tricks on offer. Holds every hand and every guess: clients and
 * robots read only `pirateViewFor` (view.ts).
 */
export interface PirateRound {
  readonly seed: number;
  readonly dealer: Seat;
  readonly controllers: BridgeControllers;
  readonly cardsPerHand: number;
  /** Lowest rank in the deck: 2 is the full deck, 8 is Big Cards (28 cards). */
  readonly minRank: Rank;
  /**
   * Cap'n's Hat: a 1-card round played with the card on the forehead. Every other seat's card is
   * visible to a player, their own is not (see view.ts). Only ever true when `cardsPerHand` is 1.
   */
  readonly hat: boolean;
  readonly hands: Readonly<Record<Seat, readonly Card[]>>;
  /** The card turned up to name trump (with 13 each, the last card dealt, the dealer's). Public. */
  readonly trumpCard: Card;
  readonly trump: Suit;
  readonly phase: PirateRoundPhase;
  /** Guesses submitted so far. Hidden from every other seat until all four are in. */
  readonly guesses: Readonly<Partial<Record<Seat, number>>>;
  readonly turn: Seat;
  readonly trick: readonly TrickPlay<Seat>[];
  readonly tricks: readonly PirateCompletedTrick[];
  readonly tricksWon: Readonly<Record<Seat, number>>;
}

export interface NewPirateRoundOptions {
  readonly seed: number;
  readonly cardsPerHand: number;
  /** Deck's lowest rank, default 2 (full deck). */
  readonly minRank?: Rank;
  /** Play a 1-card round "hat on" (ignored for any other size). Default off. */
  readonly hat?: boolean;
  readonly dealer?: Seat;
  readonly controllers?: BridgeControllers;
}

/** The most cards each of four players can hold: 13 from the full deck, 7 from Big Cards. */
export const maxCardsPerHand = (minRank: Rank = 2): number => Math.floor(fullDeck(minRank).length / 4);

export function newPirateRound(opts: NewPirateRoundOptions): PirateRound {
  const n = opts.cardsPerHand;
  const minRank = opts.minRank ?? 2;
  const maxCards = maxCardsPerHand(minRank);
  if (!Number.isInteger(n) || n < 1 || n > maxCards) throw new IllegalPirateMoveError(`cards per hand must be 1..${maxCards}, not ${n}`);
  const dealer = opts.dealer ?? 'N';
  const deck = shuffled(fullDeck(minRank), seededRandom(opts.seed));
  const order = clockwiseAfter(dealer);
  const dealt: Record<Seat, Card[]> = { N: [], E: [], S: [], W: [] };
  for (let i = 0; i < 4 * n; i++) dealt[order[i % 4] as Seat].push(deck[i] as Card);
  // With the whole deck dealt the last card dealt names trump; otherwise the next card of the deck is turned up.
  const trumpCard = (4 * n === deck.length ? deck[4 * n - 1] : deck[4 * n]) as Card;
  const hands = Object.fromEntries(BRIDGE_SEATS.map((s) => [s, sortHand(dealt[s])])) as Record<Seat, Card[]>;
  return {
    seed: opts.seed,
    dealer,
    controllers: opts.controllers ?? SINGLE_PLAYER,
    cardsPerHand: n,
    minRank,
    hat: (opts.hat ?? false) && n === 1,
    hands,
    trumpCard,
    trump: trumpCard.suit,
    phase: 'guess',
    guesses: {},
    turn: leftOf(dealer),
    trick: [],
    tricks: [],
    tricksWon: { N: 0, E: 0, S: 0, W: 0 },
  };
}

/** Seats that have not guessed yet. */
export const pendingGuessers = (round: PirateRound): Seat[] => BRIDGE_SEATS.filter((s) => round.guesses[s] === undefined);

/**
 * A seat's guess, 0..cardsPerHand. Guesses may be made in any order and cannot be changed; when the
 * fourth arrives all are revealed and play begins.
 */
export function submitGuess(round: PirateRound, seat: Seat, guess: number): PirateRound {
  if (round.phase !== 'guess') throw new IllegalPirateMoveError('guessing is over');
  if (round.guesses[seat] !== undefined) throw new IllegalPirateMoveError(`${seat} has already guessed`);
  if (!Number.isInteger(guess) || guess < 0 || guess > round.cardsPerHand) {
    throw new IllegalPirateMoveError(`a guess is 0..${round.cardsPerHand}, not ${guess}`);
  }
  const guesses = { ...round.guesses, [seat]: guess };
  const all = BRIDGE_SEATS.every((s) => guesses[s] !== undefined);
  return { ...round, guesses, phase: all ? 'play' : 'guess' };
}

export function pirateLegalPlays(round: PirateRound): Card[] {
  if (round.phase !== 'play') return [];
  return followSuit(round.hands[round.turn], round.trick[0]?.card.suit);
}

export function pirateWinnerOfTrick(round: PirateRound): Seat {
  return trickWinner(round.trick, round.trump);
}

export function piratePlayCard(round: PirateRound, card: Card): PirateRound {
  if (round.phase !== 'play') throw new IllegalPirateMoveError(`cannot play a card in the ${round.phase} phase`);
  if (!pirateLegalPlays(round).some((c) => sameCard(c, card))) {
    throw new IllegalPirateMoveError(`${round.turn} cannot play that card now`);
  }
  const seat = round.turn;
  const hands = { ...round.hands, [seat]: round.hands[seat].filter((c) => !sameCard(c, card)) };
  const trick = [...round.trick, { seat, card }];
  if (trick.length < 4) return { ...round, hands, trick, turn: leftOf(seat) };

  const winner = trickWinner(trick, round.trump);
  const tricks = [...round.tricks, { leader: (trick[0] as TrickPlay<Seat>).seat, plays: trick, winner }];
  const tricksWon = { ...round.tricksWon, [winner]: round.tricksWon[winner] + 1 };
  return {
    ...round,
    hands,
    trick: [],
    tricks,
    tricksWon,
    turn: winner,
    phase: tricks.length === round.cardsPerHand ? 'done' : 'play',
  };
}

export interface PirateSeatResult {
  readonly guess: number;
  readonly won: number;
  readonly exact: boolean;
  /** 10 + tricks won for an exact guess; any miss, over or under, scores 0. */
  readonly score: number;
}
export type PirateRoundResult = Readonly<Record<Seat, PirateSeatResult>>;

export const EXACT_BONUS = 10;

/** A round in which nobody is exact scores nothing for anyone: that is normal, not an error. */
export function pirateRoundResult(round: PirateRound): PirateRoundResult | null {
  if (round.phase !== 'done') return null;
  return Object.fromEntries(
    BRIDGE_SEATS.map((s) => {
      const guess = round.guesses[s] as number;
      const won = round.tricksWon[s];
      const exact = guess === won;
      return [s, { guess, won, exact, score: exact ? EXACT_BONUS + won : 0 }];
    }),
  ) as PirateRoundResult;
}

/**
 * Cap'n's Hat: the holder cannot see their card, so they do not choose it. This plays the only card
 * of the seat to move; the game or store calls it instead of `piratePlayCard` in a hat round.
 */
export function playHiddenCard(round: PirateRound): PirateRound {
  if (!round.hat) throw new IllegalPirateMoveError('only a hat round plays a hidden card');
  return piratePlayCard(round, round.hands[round.turn][0] as Card);
}
